import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { Package } from 'lucide-react';
import { WithdrawalsComparativeStats } from './WithdrawalsComparativeStats';

interface WithdrawalsPieChartProps {
  withdrawals: any[];
  productById: Record<string, any>;
  periodLabel?: string;
  customColor?: string;
  title?: string;
  showComparativeStats?: boolean;
}

export const WithdrawalsPieChart: React.FC<WithdrawalsPieChartProps> = ({ 
  withdrawals, 
  productById,
  periodLabel = '',
  customColor,
  title,
  showComparativeStats = false
}) => {

  const pieChartData = useMemo(() => {
    const productCounts = withdrawals.reduce((acc, retrait) => {
      const productId = retrait.product_id;
      const quantity = Number(retrait.quantity_withdrawn) || 0;
      acc[productId] = (acc[productId] || 0) + quantity;
      return acc;
    }, {} as Record<string, number>);

    // Trier tous les produits valides par quantité décroissante
    const allValidProducts = Object.entries(productCounts)
      .map(([productId, count]) => ({
        name: productById[productId]?.name || 'Produit inconnu',
        value: count,
        product: productById[productId],
        productId
      }))
      .filter(item => item.product)
      .sort((a, b) => Number(b.value) - Number(a.value));

    // Prendre les 8 premiers
    const topProducts = allValidProducts.slice(0, 8);
    
    // Calculer les "Autres" à partir des produits restants
    const otherProducts = allValidProducts.slice(8);
    const otherCount = otherProducts.reduce((sum, item) => sum + Number(item.value), 0);

    const productData = [...topProducts];

    if (otherCount > 0) {
      productData.push({
        name: 'Autres',
        value: otherCount,
        product: null,
        productId: 'others'
      });
    }

    return productData;
  }, [withdrawals, productById]);

  const pieColors = useMemo(() => {
    if (customColor) {
      // Génère des variations de la couleur personnalisée
      return [
        customColor,
        customColor + '80', // Plus clair
        customColor + '60', // Encore plus clair
        customColor + 'CC', // Semi-transparent
        customColor + 'AA', // Plus transparent
        'hsl(221 83% 53%)',  // Bleu par défaut pour les autres
        'hsl(32 95% 60%)',   // Orange
        'hsl(271 91% 65%)',  // Violet
        'hsl(210 40% 70%)'   // Gris pour "Autres"
      ];
    }
    return [
      'hsl(35 90% 55%)',   // Orange (thème retraits)
      'hsl(220 91% 42%)',  // Bleu professionnel
      'hsl(0 75% 55%)',    // Rouge
      'hsl(271 91% 65%)',  // Violet
      'hsl(142 76% 36%)',  // Vert
      'hsl(45 93% 47%)',   // Jaune
      'hsl(340 82% 52%)',  // Rose
      'hsl(200 98% 39%)',  // Cyan
      'hsl(20 90% 48%)',   // Orange brûlé
      'hsl(210 40% 60%)'   // Gris pour "Autres"
    ];
  }, [customColor]);

  return (
    <div className="space-y-6">
      <Card className="transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package 
              className="w-5 h-5" 
              style={customColor ? { color: customColor } : undefined}
            />
            <span style={customColor ? { color: customColor } : undefined}>
              {title || `Répartition des produits retirés ${periodLabel}`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pieChartData.length > 0 ? (
            <div className="h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={{
                      stroke: customColor || "hsl(var(--muted-foreground))",
                      strokeWidth: 1
                    }}
                    label={({ name, value, percent }) => {
                      return `${name} (${(percent * 100).toFixed(0)}%)`;
                    }}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="white"
                    strokeWidth={2}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: any, name: any) => [value, 'Articles retirés']}
                    labelFormatter={(label) => `Produit: ${label}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Aucune donnée disponible</p>
              <p className="text-sm">Aucun retrait trouvé pour cette période</p>
            </div>
          )}
        </CardContent>
      </Card>

      {showComparativeStats && (
        <WithdrawalsComparativeStats withdrawals={withdrawals} customColor={customColor} />
      )}
    </div>
  );
};